import cx from "clsx";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { useRouter } from "next/router";
import * as React from "react";

import PanelSearch from "./components/PanelSearch";
import styles from "./index.module.scss";

import { useDebounce } from "@/modules/common-hooks/useDebounce";
import { range } from "@/modules/common-utils/range";
import { EM_DASH } from "@/modules/common-utils/unicode";
import Checkbox from "@/modules/kreate-components/components/Checkbox";
import LazyArtworkList from "@/modules/kreate-components/components/LazyArtworkList";
import PageLayout from "@/modules/kreate-components/components/PageLayout";
import Select$ArtworkOrder, {
  ArtworkOrder,
  artworkOrderItems,
} from "@/modules/kreate-components/components/Select$ArtworkOrder";
import Select$MintStatus, {
  MintStatus,
} from "@/modules/kreate-components/components/Select$MintStatus";

const ITEMS_PER_PAGE = 12;

type Props = {
  className?: string;
  style?: React.CSSProperties;
};

export default function PageExploreArtworks({ className, style }: Props) {
  const router = useRouter();
  const [pageCount, setPageCount] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState(
    typeof router.query.q === "string" ? router.query.q : ""
  );
  const [debouncedSearchInput] = useDebounce(searchInput, { delay: 500 });

  const parsedMintingStatus = MintStatus.safeParse(router.query.mintStatus);

  const initialMintStatus: MintStatus = parsedMintingStatus.success
    ? parsedMintingStatus.data
    : "available_for_minting";

  const [mintStatus, setMintStatus] =
    React.useState<MintStatus>(initialMintStatus);
  const [artworkOrder, setArtworkOrder] =
    React.useState<ArtworkOrder>("newest");
  const [isSingleEdition, setIsSingleEdition] = React.useState(false);
  const [isOwnerVerified, setIsOwnerVerified] = React.useState(false);

  const { orderBy, orderDirection } = artworkOrderItems[artworkOrder];

  const [endOfResults, setEndOfResults] = React.useState(false);
  const [totalNumArtworks, setTotalNumArtworks] = React.useState<
    number | null | undefined
  >(null);

  React.useEffect(() => {
    setPageCount(1);
    setEndOfResults(false);
  }, [
    debouncedSearchInput,
    mintStatus,
    artworkOrder,
    isSingleEdition,
    isOwnerVerified,
  ]);

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PageLayout
        style={{ minHeight: "120vh" }}
        title="Explore Artworks | Kreate"
        background="light"
        hideSearchBarOnNavbar
        shouldUseNavbarV3
      >
        <PageLayout.Section size="sm" marginTop="48px" marginBottom="48px">
          <PanelSearch
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            state="artworks"
          />
        </PageLayout.Section>
        <PageLayout.Section marginTop="48px" marginBottom="36px">
          <Flex.Row
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="12px"
          >
            <h1 className={styles.title}>
              <span>
                {totalNumArtworks != null
                  ? `${totalNumArtworks} Artworks`
                  : EM_DASH}
              </span>
            </h1>
            <Flex.Row gap="16px" alignItems="center" flexWrap="wrap">
              <Checkbox
                label="1/1"
                title="Single Edition Artwork"
                value={isSingleEdition}
                onChange={setIsSingleEdition}
              />
              <Checkbox
                label="From Verified Artist"
                value={isOwnerVerified}
                onChange={setIsOwnerVerified}
              />
              <Divider.Vertical className={styles.divider} />
              <Flex.Row gap="12px" alignItems="center">
                <Select$MintStatus
                  value={mintStatus}
                  onChange={(value) => setMintStatus(value)}
                />
                <Select$ArtworkOrder
                  value={artworkOrder}
                  onChange={(value) => setArtworkOrder(value)}
                />
              </Flex.Row>
            </Flex.Row>
          </Flex.Row>
        </PageLayout.Section>
        <PageLayout.Divider />
        <PageLayout.Section style={{ marginTop: "64px" }}>
          <Flex.Col
            gap="56px"
            key={
              `${debouncedSearchInput} ${mintStatus}` +
              ` ${artworkOrder} ${isSingleEdition} ${isOwnerVerified}`
            }
          >
            {range(pageCount).map((index) => (
              <LazyArtworkList
                key={index}
                params={{
                  searchQuery: debouncedSearchInput,
                  offset: index * ITEMS_PER_PAGE,
                  limit: ITEMS_PER_PAGE,
                  isSingleEdition: isSingleEdition || undefined,
                  isOwnerVerified: isOwnerVerified || undefined,
                  orderBy,
                  orderDirection,
                  mintStatus,
                }}
                onLoad={({ maybeHasMore, totalNumArtworks }) => {
                  if (index == 0) {
                    setTotalNumArtworks(totalNumArtworks);
                  }
                  if (maybeHasMore) {
                    setPageCount((value) => Math.max(value, index + 2));
                  } else {
                    setEndOfResults(true);
                  }
                }}
              />
            ))}
            {endOfResults ? (
              <div className={styles.textEndOfResults}>{"End of results."}</div>
            ) : null}
          </Flex.Col>
        </PageLayout.Section>
      </PageLayout>
    </div>
  );
}
